import './styles/globals.css';
import { setTheme } from '@kk/ui/hooks/useTheme';
import i18n, { initI18n } from '@kk/ui/i18n';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '#/App';
import msalInstance from '#/api/auth/msalInstance';
import { resources } from '#/locales/resources';
import { clearCache, resolveQueryCacheStatus } from '#/state';
import { isBypassLoginEnabled } from '#/utils/isBypassLoginEnabled';
import { handleTokenError } from './api/errors';
import { setupDevelopmentWebSocket } from './websocket';

if (isBypassLoginEnabled()) {
  initializeWithBypassLogin();
} else {
  initializeWithAuthentication();
}

async function initializeApp() {
  setTheme('laane');
  initializeI18n();
  renderApp();
}

function initializeI18n() {
  if (!i18n.isInitialized) {
    initI18n({
      lng: 'da',
      resources,
    });
  }
}

function renderApp() {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

async function initializeWithBypassLogin() {
  try {
    await setupMockAuthentication();
    await setupDevelopmentEnvironment();
    await initializeApp();
  } catch (error) {
    console.error('Failed to initialize with bypass login:', error);
  }
}

async function initializeWithAuthentication() {
  try {
    await msalInstance.initialize();
    await handleAuthenticationFlow();
    await initializeApp();
  } catch (error) {
    handleTokenError(error);
  }
}

async function handleAuthenticationFlow() {
  const [tokenResponse] = await Promise.all([
    msalInstance.handleRedirectPromise(),
    resolveQueryCacheStatus(),
  ]);

  if (tokenResponse) {
    msalInstance.setActiveAccount(tokenResponse.account);
  }

  if (import.meta.env.DEV) {
    await setupDevelopmentEnvironment();
  }
}

async function setupMockAuthentication() {
  const [{ mockAccount, mockResult }] = await Promise.all([
    import('#/api/mocks/mockData/auth/test-constants'),
  ]);

  msalInstance.setActiveAccount(mockAccount);
  msalInstance.acquireTokenSilent = async () => mockResult;
  msalInstance.getActiveAccount = () => mockAccount;
}

async function setupDevelopmentEnvironment() {
  const [{ mockResult }] = await Promise.all([
    import('#/api/mocks/mockData/auth/test-constants'),
  ]);

  msalInstance.acquireTokenSilent = async () => mockResult;
  setupDevelopmentWebSocket(clearCache);
}
